import { OrgRole } from '@grafana/data';
import { config } from '@grafana/runtime';

import { getContextSrv } from 'hooks';

const PluginID = 'grafana-ml-app';

export interface UserAction {
  permission: string;
  fallbackMinimumRoleRequired: OrgRole;
}

export enum Resource {
  FORECASTING = 'forecasting',
  OUTLIERS = 'outliers',
  SIFT = 'sift',
  ALERT_RULES = 'alert.rules',
}

export enum Action {
  READ = 'read',
  WRITE = 'write',
  CREATE = 'create',
}

type Actions =
  | 'ForecastingRead'
  | 'ForecastingWrite'
  | 'OutliersRead'
  | 'OutliersWrite'
  | 'SiftRead'
  | 'SiftWrite'
  | 'AlertRulesCreate';

const roleMapping: Record<OrgRole, number> = {
  [OrgRole.Admin]: 0,
  [OrgRole.Editor]: 1,
  [OrgRole.Viewer]: 2,
  [OrgRole.None]: 3,
};

/**
 * The logic here is:
 * - an Admin should be able to do everything (including whatever an Editor and Viewer can do)
 * - an Editor should be able to do things Editors and Viewers can do
 * - a Viewer is only allowed to do things Viewers can do
 */
export const userHasMinimumRequiredRole = (minimumRoleRequired: OrgRole): boolean =>
  roleMapping[getContextSrv().user.orgRole as OrgRole] <= roleMapping[minimumRoleRequired];

/**
 * See here for more info on the hasAccess method
 * https://github.com/grafana/grafana/blob/main/public/app/core/services/context_srv.ts#L165-L170
 *
 * As a fallback (second argument), for cases where RBAC is not enabled for a grafana instance, rely on basic roles
 */
export const isUserActionAllowed = ({ permission, fallbackMinimumRoleRequired }: UserAction): boolean => {
  // if RBAC is enabled
  if (config.featureToggles.accessControlOnCall === true) {
    // if user has permission, we allow the action
    if (getContextSrv().user.permissions?.[permission] === true) {
      return true;
    }
  }

  // otherwise we check basic roles
  return userHasMinimumRequiredRole(fallbackMinimumRoleRequired);
};

/**
 * Given a `UserAction`, returns the permission or fallback-role, prefixed with "permission" or "role" respectively
 * depending on whether or not RBAC is enabled/disabled
 */
export const determineRequiredAuthString = ({ permission, fallbackMinimumRoleRequired }: UserAction): string =>
  config.featureToggles.accessControlOnCall === true
    ? `${permission} permission`
    : `${fallbackMinimumRoleRequired} role`;

/**
 * Can be used to generate a user-friendly message about which permission is missing. Method is RBAC-aware
 * and shows user the missing permission/basic-role depending on whether or not RBAC is enabled/disabled
 */
export const generateMissingPermissionMessage = (permission: UserAction): string =>
  `You are missing the ${determineRequiredAuthString(permission)}`;

export const generatePermissionString = (resource: Resource, action: Action, includePrefix: boolean): string =>
  `${includePrefix ? `${PluginID}.` : ''}${resource}:${action}`;

const constructAction = (
  resource: Resource,
  action: Action,
  fallbackMinimumRoleRequired: OrgRole,
  includePrefix = true
): UserAction => ({
  permission: generatePermissionString(resource, action, includePrefix),
  fallbackMinimumRoleRequired,
});

export const UserActions: Record<Actions, UserAction> = {
  ForecastingRead: constructAction(Resource.FORECASTING, Action.READ, OrgRole.Viewer),
  ForecastingWrite: constructAction(Resource.FORECASTING, Action.WRITE, OrgRole.Editor),
  OutliersRead: constructAction(Resource.OUTLIERS, Action.READ, OrgRole.Viewer),
  OutliersWrite: constructAction(Resource.OUTLIERS, Action.WRITE, OrgRole.Editor),
  SiftRead: constructAction(Resource.SIFT, Action.READ, OrgRole.Viewer),
  SiftWrite: constructAction(Resource.SIFT, Action.WRITE, OrgRole.Editor),
  AlertRulesCreate: constructAction(Resource.ALERT_RULES, Action.CREATE, OrgRole.Editor, false),
};
